export const PANEL_CLASSES =
  'bg-white rounded-lg border border-ui-divider-lines overflow-hidden flex flex-col';

export const HEADER_CLASSES =
  'px-3.5 py-2.5 font-semibold text-sm text-ui-text-primary-text border-b border-ui-divider-lines bg-unlisted-very-light-gray flex justify-between items-center';

// No margin of its own: rows centre it beside icons and buttons, and the
// stand-alone Core Fields title adds `block mb-2` itself.
export const SECTION_LABEL =
  'text-sm font-bold leading-5 text-ui-text-primary-text';

// Empty-slot text and fill states. The dashed outline itself is the
// .slot-dashed / .slot-dashed-hot background in index.css.
export const DROP_ZONE_ACTIVE = 'bg-teal-t10 text-ui-cta-primary';

export const DROP_ZONE_IDLE = 'text-ui-text-primary-text';

// Empty drop zones while a source leaf is armed for click-mapping — signals "click me".
export const DROP_ZONE_TARGET_HOT =
  'text-ui-cta-primary animate-pulse cursor-pointer';

// ── Per-field transforms ──
// Purple is the accent for everything transform-related (wand, pills, transformed preview),
// so the feature reads as one distinct system against the green/blue mapping UI.

// Small wand button inside a mapping chip that opens the transform picker.
export const TRANSFORM_ADD_BTN =
  'text-unlisted-purple hover:text-unlisted-dark-purple leading-none px-1 py-0.5 rounded shrink-0';
export const TRANSFORM_ADD_BTN_ACTIVE =
  'text-unlisted-dark-purple bg-unlisted-light-purple leading-none px-1 py-0.5 rounded shrink-0';
// Shown (not hidden) when the filter is complex, so the feature stays discoverable —
// paired with a tooltip explaining why it's unavailable.
export const TRANSFORM_ADD_BTN_DISABLED =
  'text-ui-text-disabled cursor-not-allowed leading-none px-1 py-0.5 rounded shrink-0 inline-flex';

// A single applied-transform pill shown beneath a mapping chip
// (icon + monospace summary + subtle ×).
export const TRANSFORM_PILL =
  'inline-flex items-center gap-1.5 font-mono text-[11px] bg-unlisted-light-purple text-unlisted-dark-purple rounded-full px-2.5 py-0.5';

// Transformed value shown after the pills (`= 2024-06-26T…`).
export const TRANSFORM_PREVIEW =
  'text-unlisted-dark-purple text-[11px] max-w-[160px] overflow-hidden text-ellipsis whitespace-nowrap';

// Portaled grouped picker popover.
export const TRANSFORM_MENU =
  'bg-white border border-ui-divider-lines rounded shadow-lg overflow-hidden py-1';
export const TRANSFORM_MENU_SECTION =
  'text-[10px] font-bold text-ui-text-primary-text uppercase tracking-wide px-3 pt-2 pb-1';
export const TRANSFORM_MENU_ITEM =
  'w-full flex items-center justify-between gap-3 px-3 py-1.5 text-xs text-left text-ui-text-primary-text transition-colors focus:outline-none hover:bg-unlisted-light-purple';

// ── Stacked slot layout (label on its own line, slot full width below) ──

// The focusable wrapper of every builder row (core, additional, condition):
// keyboard picks return focus here once the slot is mapped.
export const SLOT_ROW =
  'mb-4 rounded focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ui-cta-primary';

export const SLOT_LABEL =
  'mb-1.5 flex items-center font-mono text-xs font-medium text-ui-text-primary-text';

// Mapped chip: light-gray fill, no border, path + value on the left and the
// controls (× | wand) on the right inside the same box.
export const CHIP =
  'flex min-h-[36px] w-full items-center gap-2 rounded-md bg-teal-t10 px-3 py-1.5 font-mono text-xs text-ui-text-primary-text';
export const CHIP_WARNING =
  'flex min-h-[36px] w-full items-center gap-2 rounded-md bg-unlisted-warning-yellow px-3 py-1.5 font-mono text-xs text-yellow-1';

// Vertical rule between the × and the wand inside a chip or empty slot.
export const CHIP_DIVIDER = 'mx-1 h-5 w-px shrink-0 bg-ui-divider-lines';

// Empty slot: dashed full-width box, placeholder left, wand right.
// The dash pattern itself comes from .slot-dashed / .slot-dashed-hot in
// index.css: CSS border-dashed cannot make dashes shorter, an SVG stroke can.
export const EMPTY_SLOT =
  'flex min-h-[36px] w-full items-center rounded-md transition-all';
// The focus ring is drawn by EMPTY_SLOT's wrapper (see EmptySlot), so the
// target itself only drops the browser outline.
export const EMPTY_SLOT_TEXT =
  'flex-1 px-3 py-1.5 text-left text-xs focus:outline-none';
